import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useDateLocale } from "@/hooks/useDateLocale";
import type { TimesheetWeek, TimekeepingTask } from "@/lib/api";
import type { HoursMap } from "@/components/timekeeping/hoursMap";

interface Props {
  week: TimesheetWeek;
  tasks: TimekeepingTask[];
  editable: boolean;
  value: HoursMap;
  onChange: (next: HoursMap) => void;
}

export function WeeklyGrid({ week, tasks, editable, value, onChange }: Props) {
  const { t } = useTranslation();
  const locale = useDateLocale();

  const dates = week.days.map((d) => d.date);
  const leaveByDate = useMemo(() => {
    const m: Record<string, string | null> = {};
    for (const d of week.days) m[d.date] = d.leave?.type ?? null;
    return m;
  }, [week]);
  const dayMeta = useMemo(() => {
    const m: Record<string, { holiday: boolean; out: boolean }> = {};
    for (const d of week.days) m[d.date] = { holiday: !!d.holiday, out: !!d.outOfEmployment };
    return m;
  }, [week]);
  // Days on which time cannot be logged: approved leave or outside employment.
  const isLockedDay = (date: string) => !!leaveByDate[date] || !!dayMeta[date]?.out;

  // When editable, show a row for EVERY active task so the week is immediately
  // fillable — no "add row" step. Read-only weeks show only rows with entries.
  const rowTaskIds = useMemo(() => {
    const ids: string[] = [];
    if (editable) for (const tk of tasks) ids.push(String(tk.id));
    for (const k of Object.keys(value)) if (k !== "none" && !ids.includes(k)) ids.push(k);
    if (Object.keys(value).includes("none")) ids.push("none");
    return ids;
  }, [tasks, value, editable]);

  const taskName = (key: string) =>
    key === "none" ? t("timekeeping.entry.noTask", { defaultValue: "No task" })
      : tasks.find((tk) => String(tk.id) === key)?.name ?? `#${key}`;
  const taskColor = (key: string) => tasks.find((tk) => String(tk.id) === key)?.color ?? "#94a3b8";

  const setCell = (taskKey: string, date: string, raw: string) => {
    if (isLockedDay(date)) return;
    const n = raw === "" ? 0 : Math.max(0, Math.min(24, Number(raw)));
    const next: HoursMap = { ...value, [taskKey]: { ...(value[taskKey] || {}) } };
    if (Number.isNaN(n) || n === 0) delete next[taskKey][date];
    else next[taskKey][date] = n;
    onChange(next);
  };

  const dayTotal = (date: string) =>
    rowTaskIds.reduce((s, k) => s + (value[k]?.[date] || 0), 0);
  const rowTotal = (taskKey: string) =>
    dates.reduce((s, d) => s + (value[taskKey]?.[d] || 0), 0);

  return (
    <div className="min-w-0 max-w-full overflow-x-auto rounded-lg border">
      <table className="w-full min-w-[736px] border-collapse text-sm">
        <thead>
          <tr className="bg-muted/40">
            <th className="text-left font-medium px-3 py-2 min-w-[160px]">{t("timekeeping.entry.task", { defaultValue: "Task" })}</th>
            {week.days.map((d) => (
              <th key={d.date} className="px-2 py-2 text-center font-medium min-w-[64px]">
                <div className="text-xs">{format(new Date(d.date + "T00:00:00"), "EEE", { locale })}</div>
                <div className="text-[11px] text-muted-foreground">{format(new Date(d.date + "T00:00:00"), "d", { locale })}</div>
                {leaveByDate[d.date] && (
                  <Badge variant="secondary" className="mt-1 text-[9px] px-1 py-0 bg-purple-500/10 text-purple-600 dark:text-purple-400 border-0">
                    {t(`timekeeping.leaveTypes.${leaveByDate[d.date]}`, { defaultValue: leaveByDate[d.date] as string })}
                  </Badge>
                )}
                {!leaveByDate[d.date] && dayMeta[d.date]?.out && (
                  <Badge variant="secondary" className="mt-1 text-[9px] px-1 py-0 bg-muted text-muted-foreground border-0">
                    {t("timekeeping.entry.notEmployed", { defaultValue: "—" })}
                  </Badge>
                )}
                {!leaveByDate[d.date] && !dayMeta[d.date]?.out && dayMeta[d.date]?.holiday && (
                  <Badge variant="secondary" className="mt-1 text-[9px] px-1 py-0 bg-blue-500/10 text-blue-600 dark:text-blue-400 border-0">
                    {t("timekeeping.entry.holiday", { defaultValue: "Holiday" })}
                  </Badge>
                )}
              </th>
            ))}
            <th className="px-2 py-2 text-center font-medium min-w-[64px]">{t("timekeeping.week.total", { defaultValue: "Total" })}</th>
          </tr>
        </thead>
        <tbody>
          {rowTaskIds.length === 0 && (
            <tr>
              <td colSpan={week.days.length + 2} className="px-3 py-6 text-center text-muted-foreground">
                {editable
                  ? t("timekeeping.week.noTasksDefined", { defaultValue: "No tasks are defined yet. Ask HR to add tasks." })
                  : t("timekeeping.week.noneLogged", { defaultValue: "No time logged." })}
              </td>
            </tr>
          )}
          {rowTaskIds.map((taskKey) => (
            <tr key={taskKey} className="border-t">
              <td className="px-3 py-1.5">
                <span className="inline-flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: taskColor(taskKey) }} />
                  <span className="truncate">{taskName(taskKey)}</span>
                </span>
              </td>
              {dates.map((date) => (
                <td key={date} className="px-1 py-1 text-center">
                  <Input
                    type="number" min={0} max={24} step={0.25}
                    disabled={!editable || isLockedDay(date)}
                    value={value[taskKey]?.[date] ?? ""}
                    onChange={(e) => setCell(taskKey, date, e.target.value)}
                    className="h-8 w-14 text-center px-1 tabular-nums"
                  />
                </td>
              ))}
              <td className="px-2 py-1 text-center font-medium tabular-nums">{rowTotal(taskKey) || ""}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t bg-muted/30 font-medium">
            <td className="px-3 py-2">{t("timekeeping.week.dailyTotal", { defaultValue: "Daily total" })}</td>
            {dates.map((date) => (
              <td key={date} className="px-2 py-2 text-center tabular-nums">{dayTotal(date) || "0"}</td>
            ))}
            <td className="px-2 py-2 text-center tabular-nums">{rowTaskIds.reduce((s, k) => s + rowTotal(k), 0) || "0"}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
